<script setup lang="ts">
import AppButton from '@/components/base/AppButton.vue';

const {
  backTo,
  absolute = false,
  backButton = true,
  customBackHandler = false,
} = defineProps<{
  backTo?: string | (() => void);
  absolute?: boolean;
  backButton?: boolean;
  customBackHandler?: boolean;
}>();

interface Emits {
  (e: 'back'): void;
}

const emit = defineEmits<Emits>();

const { isMobile } = useScreenSize();

const router = useRouter();

const handleBack = async () => {
  // Emit the back event for parent components to handle
  emit('back');

  // If no custom back handler is specified, use default navigation
  if (!customBackHandler) {
    if (typeof backTo === 'function') {
      backTo();
    } else {
      backTo ? await navigateTo(backTo) : router.back();
    }
  }
};
</script>

<template>
  <div class="browse-header-container" :class="{ 'header-absolute': absolute }">
    <header class="browse-header d-flex align-center">
      <AppButton
        variant="text"
        size="small"
        rounded="xl"
        color="-primary"
        class="back-button position-absolute"
        @click="handleBack"
        v-if="backButton"
      >
        <v-icon icon="mdi-chevron-left" size="20" />
      </AppButton>

      <div class="header-title flex-grow-1">
        <slot name="title"></slot>
      </div>

      <div class="right-panel">
        <slot name="right-panel"></slot>
      </div>
    </header>

    <div class="bottom-divider"></div>
  </div>
</template>

<style lang="scss" scoped>
.browse-header-container {
  background-color: rgb(var(--v-theme-background));

  &.header-absolute {
    position: absolute;
    width: 100%;
    left: 0;
    top: 0;
    padding-inline: 16px;
    
    .bottom-divider {
      display: none !important;
    }

    @media (max-width: 768px) {
      .back-button {
        min-height: 32px;
        min-width: 32px;
        padding: 0px;
        background-color: rgb(var(--v-theme-surface));
        box-shadow:
          0px 2px 1px -1px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
          0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)),
          0px 1px 3px 0px var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.12)) !important;
      }
    }
  }
}

.bottom-divider {
  display: none;
  position: absolute;
  top: 50;
  left: 0;
  width: 100%;
  border-bottom: 0.5px solid rgb(var(--v-theme-stroke));

  @media (max-width: 768px) {
    display: block;
  }
}

.browse-header {
  min-height: 50px;
  padding: 14px;
  background-color: rgb(var(--v-theme-background));

  &.browsing {
    padding-top: 24px;

    @media (max-width: 768px) {
      padding-top: 14px;
    }
  }

  .back-button {
    min-height: 32px;
    min-width: 32px;
    padding: 0px;
    background-color: rgb(var(--v-theme-neutral));
  }

  .header-title {
    margin-inline-start: 50px;
    min-width: 0;
  }
}

@media (max-width: 768px) {
  .browse-header {
    padding-inline: 0px;
    width: 100%;

    .back-button {
      min-height: 24px;
      min-width: 24px;
      background-color: transparent;
      display: block;
      height: 24px;

      .v-icon {
        font-size: 24px !important;
      }
    }

    .header-title {
      text-align: center;
      margin-inline-start: 0px;
    }
  }
}
</style>
